import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatMalaysiaDateTimeStamp } from '../../utils/dateTime';
import { BarList, Table } from './StatistikParts';

// Laporan & Data Statistik (URS item 16): the seven statistics requested by JAIS on one page.
const emptySummary = {
    totals: {},
    aj_classification: [],
    by_district: [],
    by_offense: [],
    without_offense: 0,
    arrests: { by_district: [] },
    arahan_beredar: { by_district: [] },
};

const emptyFilters = { district: '', fromDate: '', toDate: '' };

// `view` (URS 2.0 item 1) splits this combined report across the Data & Statistik submenus:
//   'aduan' = items 1-3, 'kes' = items 4-6, 'ab' = item 8. Undefined shows everything (legacy route).
const VIEW_META = {
    aduan: { title: 'Statistik Aduan', desc: 'Jumlah keseluruhan aduan, klasifikasi Aduan Jenayah dan aduan mengikut daerah.' },
    kes: { title: 'Statistik Kes', desc: 'Aduan mengikut seksyen kesalahan, kes tangkapan dan jumlah tangkapan OYDS mengikut daerah.' },
    ab: { title: 'Statistik Arahan Beredar', desc: 'Rekod Arahan Beredar dan jumlah OYDS mengikut daerah.' },
};

const ComplaintStatistikReport = ({ view }) => {
    const showAduan = !view || view === 'aduan';
    const showKes = !view || view === 'kes';
    const showAb = !view || view === 'ab';
    const meta = VIEW_META[view] || { title: 'Laporan dan Data Statistik', desc: 'Klasifikasi aduan, aduan mengikut daerah dan seksyen, kes tangkapan, OYDS dan Arahan Beredar.' };
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const role = String(localStorage.getItem('role') || '').trim().toLowerCase();
    const isDistrictOfficer = role === 'pegawai_daerah';
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);
    const [summary, setSummary] = useState(emptySummary);
    const [filters, setFilters] = useState(emptyFilters);
    // Only the latest request may update the page; an older, slower response must not overwrite it.
    const latestRequestRef = useRef(0);

    useEffect(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/districts`)
            .then((response) => setDistrictOptions(response?.data?.data || []))
            .catch(() => setDistrictOptions([]));
    }, [apiUrl]);

    const params = useMemo(() => ({
        ...(filters.district ? { district_id: filters.district } : {}),
        ...(filters.fromDate ? { from_date: filters.fromDate } : {}),
        ...(filters.toDate ? { to_date: filters.toDate } : {}),
    }), [filters]);

    const loadSummary = () => {
        if (!apiUrl) {
            setError('API URL tidak diset.');
            setIsLoading(false);
            return;
        }
        const requestId = latestRequestRef.current + 1;
        latestRequestRef.current = requestId;
        setIsLoading(true);
        setError('');
        axios.get(`${apiUrl}/complaints/report/statistik`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params,
        })
            .then((response) => {
                if (requestId === latestRequestRef.current) setSummary({ ...emptySummary, ...(response?.data?.data || {}) });
            })
            .catch((err) => {
                if (requestId === latestRequestRef.current) setError(err?.response?.data?.message || 'Gagal memuatkan laporan statistik.');
            })
            .finally(() => {
                if (requestId === latestRequestRef.current) setIsLoading(false);
            });
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, params]);

    const handleExport = async () => {
        if (!apiUrl) return;
        setIsExporting(true);
        try {
            const response = await axios.get(`${apiUrl}/complaints/report/statistik/export/xlsx`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                params,
                responseType: 'blob',
            });
            const disposition = response?.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/i);
            const filename = match?.[1] || `laporan-statistik-${formatMalaysiaDateTimeStamp()}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Gagal export Excel laporan statistik.');
        } finally {
            setIsExporting(false);
        }
    };

    const totals = summary.totals || {};
    const arrests = summary.arrests || emptySummary.arrests;
    const ab = summary.arahan_beredar || emptySummary.arahan_beredar;
    const offenseRows = summary.by_offense || [];
    const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);

    return (
        <div className="app-section">
            <div className="app-section-header">
                <div>
                    <div className="app-section-eyebrow">DATA &amp; STATISTIK</div>
                    <h2>{meta.title}</h2>
                    <p>{meta.desc}</p>
                </div>
                <Link className="app-button app-button-ghost" to="/app/complaints/report">
                    Kembali Dashboard
                </Link>
            </div>

            <div className="app-card">
                <div className="app-tab-panel">
                    <div className="app-form-grid">
                        {!isDistrictOfficer && (
                            <label className="app-form-field">
                                <span>Daerah</span>
                                <select id="statistik-district" value={filters.district} onChange={(e) => setFilters((prev) => ({ ...prev, district: e.target.value }))}>
                                    <option value="">Semua</option>
                                    {districtOptions.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="app-form-field">
                            <span>Dari Tarikh</span>
                            <input id="statistik-from" type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
                        </label>
                        <label className="app-form-field">
                            <span>Hingga Tarikh</span>
                            <input id="statistik-to" type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
                        </label>
                        <div className="app-form-actions app-span-full">
                            <button type="button" className="app-button app-button-ghost" onClick={() => setFilters(emptyFilters)}>
                                Reset
                            </button>
                            <button type="button" className="app-button app-button-ghost" onClick={handleExport} disabled={isExporting}>
                                {isExporting ? 'Sedang Export...' : 'Export Excel'}
                            </button>
                            <button type="button" className="app-button" onClick={loadSummary}>
                                Refresh
                            </button>
                        </div>
                    </div>
                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                        Aduan ditapis mengikut tarikh aduan. Tangkapan ditapis mengikut Tarikh / Masa Tindakan di Lokasi pada rekod kes. Arahan Beredar ditapis mengikut tarikh kejadian, dan daerahnya diambil daripada rekod pegawai.
                    </p>

                    {error && <div className="app-empty">{error}</div>}
                    {isLoading && <div className="app-empty">Memuatkan statistik...</div>}

                    {!isLoading && !error && (
                        <div className="app-report" style={{ marginTop: '1rem' }}>
                            {showAduan && (<>
                            <div className="app-report-kpis">
                                <div className="app-report-kpi"><span>Keseluruhan Aduan</span><strong>{totals.complaints ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Aduan Jenayah (AJ)</span><strong>{totals.aj ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Aduan Keluarga (AK)</span><strong>{totals.ak ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Kes Tangkapan</span><strong>{arrests.arrest_cases ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Jumlah Tangkapan (OYDS)</span><strong>{arrests.arrested_total ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Rekod Arahan Beredar</span><strong>{ab.total ?? 0}</strong></div>
                            </div>

                            <div className="app-report-card">
                                <h4>Klasifikasi Status Aduan AJ</h4>
                                <BarList rows={summary.aj_classification || []} emptyText="Tiada aduan AJ untuk tapisan semasa." />
                            </div>

                            <div className="app-report-card">
                                <h4>Aduan AJ dan AK Mengikut Daerah</h4>
                                <Table
                                    columns={[
                                        { key: 'district_name', label: 'Daerah', width: '1fr', render: (row) => row.district_name },
                                        { key: 'aj', label: 'AJ' },
                                        { key: 'ak', label: 'AK' },
                                        { key: 'total', label: 'Jumlah' },
                                    ]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `district-${row.district_id || 'none'}`}
                                    footer={{ district_name: 'Jumlah', aj: totals.aj ?? 0, ak: totals.ak ?? 0, total: totals.complaints ?? 0 }}
                                />
                            </div>
                            </>)}

                            {showKes && (<>
                            <div className="app-report-card">
                                <h4>Aduan Mengikut Seksyen Kesalahan</h4>
                                {offenseRows.length ? (
                                    <Table
                                        columns={[
                                            { key: 'category', label: 'Jenis', width: '0.3fr', render: (row) => row.category || '-' },
                                            { key: 'section', label: 'Seksyen', width: '0.7fr', render: (row) => row.section || '-' },
                                            { key: 'name', label: 'Kesalahan', width: '1.6fr', render: (row) => row.name },
                                            { key: 'total', label: 'Bilangan', width: '0.35fr' },
                                        ]}
                                        rows={offenseRows}
                                        rowKey={(row) => `offense-${row.id}`}
                                        footer={{ name: 'Jumlah', total: sum(offenseRows, 'total') }}
                                    />
                                ) : (
                                    <div className="app-empty">Tiada aduan berseksyen untuk tapisan semasa.</div>
                                )}
                                {Number(summary.without_offense || 0) > 0 && (
                                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                                        {summary.without_offense} aduan belum dipilih kesalahan dan tidak dikira dalam jadual ini.
                                    </p>
                                )}
                            </div>

                            <div className="app-report-card">
                                <h4>Kes Tangkapan dan Jumlah Tangkapan (OYDS) Mengikut Daerah</h4>
                                <Table
                                    columns={[
                                        { key: 'district_name', label: 'Daerah', width: '1fr', render: (row) => row.district_name },
                                        { key: 'all_cases', label: 'Jumlah Kes' },
                                        { key: 'arrest_cases', label: 'Kes Tangkapan' },
                                        { key: 'male', label: 'Lelaki' },
                                        { key: 'female', label: 'Perempuan' },
                                        { key: 'other', label: 'Lain-lain' },
                                        { key: 'arrested_total', label: 'Jumlah OYDS' },
                                    ]}
                                    rows={arrests.by_district || []}
                                    rowKey={(row) => `arrest-${row.district_id || 'none'}`}
                                    footer={{
                                        district_name: 'Jumlah',
                                        all_cases: arrests.all_cases ?? 0,
                                        arrest_cases: arrests.arrest_cases ?? 0,
                                        male: arrests.male ?? 0,
                                        female: arrests.female ?? 0,
                                        other: arrests.other ?? 0,
                                        arrested_total: arrests.arrested_total ?? 0,
                                    }}
                                />
                            </div>
                            </>)}

                            {showAb && (
                            <div className="app-report-card">
                                <h4>Rekod Keseluruhan Arahan Beredar (AB)</h4>
                                <Table
                                    columns={[
                                        { key: 'district_name', label: 'Daerah', width: '1fr', render: (row) => row.district_name },
                                        { key: 'total', label: 'Rekod AB' },
                                        { key: 'oyds', label: 'OYDS' },
                                    ]}
                                    rows={ab.by_district || []}
                                    rowKey={(row) => `ab-${row.district_id || 'none'}`}
                                    footer={{ district_name: 'Jumlah', total: ab.total ?? 0, oyds: ab.oyds ?? 0 }}
                                />
                            </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplaintStatistikReport;
